import axios from 'axios'
import constants from './constants'
import { getToken } from './token'
import { verifyAPIVersion } from './version'

const DEFAULT_DS_YEAR_LANDSCAN = '2016'
const DEFAULT_DS_YEAR_WORLDPOP = '2024'

/** LandScan uses the selected year; WorldPop always uses 2024 (earliest configured year on backend). */
export const resolveDsHeaders = (config = {}) => {
  const dsName = (config.headers && config.headers['Ds-Name']) ||
    axios.defaults.headers.common['Ds-Name'] ||
    'landscan'
  let dsYear = (config.headers && config.headers['Ds-Year']) ||
    axios.defaults.headers.common['Ds-Year'] ||
    DEFAULT_DS_YEAR_LANDSCAN
  if (dsName === 'worldpop') {
    dsYear = DEFAULT_DS_YEAR_WORLDPOP
  }
  return {
    'Ds-Name': dsName,
    'Ds-Year': String(dsYear),
  }
}

// For every API request, add token authentication,
// and check to make sure X-API-VERSION header hasn't changed
// since this session began.
export const baseConfig = (config = {}) => ({
  baseURL: `${constants.SERVER}api/`,
  withCredentials: true,
  transformResponse: [verifyAPIVersion].concat(
    axios.defaults.transformResponse
  ),
  ...config,
  // Merged *after* the ...config spread so a caller-supplied `headers`
  // object (e.g. Ds-Name/Ds-Year) can't silently wipe out the Authorization
  // header - plain object spread doesn't deep-merge nested keys, so
  // `headers` previously had to be computed first to have any effect,
  // and any config with its own `headers` overwrote it entirely.
  headers: {
    ...(getToken() ? { Authorization: `Token ${getToken()}` } : {}),
    ...(config.headers || {})
  }
})

export const baseConfigNoAutorization = (config) => ({
  baseURL: `${constants.SERVER}api/`,
  withCredentials: true,
  headers: {},
  transformResponse: [verifyAPIVersion].concat(
    axios.defaults.transformResponse
  ),
  ...config
})
const getNoAutorization = (url, config = {}) =>
  axios.get(url, baseConfigNoAutorization(config))

const get = (url, config = {}) =>
  axios.get(url, baseConfig(config))

const post = (url, data, config) =>
  axios.post(url, data, baseConfig(config))

const patch = (url, data, config = {}) =>
  axios.patch(url, data, baseConfig(config))

const del = (url, config = {}) =>
  axios.delete(url, baseConfig(config))

export default {
  getUser: config => (
    get('rest-auth/user/', config)
  ),
  generatePortalLink: (userId, config) => (
    get(`users/generate-portal-link/${userId}`, config)
  ),
  updateUserIsFirstLogin: (data, config) => (
    post('rest-auth/is-first-login/', data, config)
  ),
  updatedIsAdvertisePopupVisible: (data, config) => (
    post('rest-auth/user/', data, config)
  ),
  getLicenseGroup: config => (
    get('users/license-group/', config)
  ),
  login: (data, config) => (
    post('rest-auth/login/', data, config)
  ),
  recalculateUserPolygons: (id, config) => (
    get(`users/user-recalculate/${id}/`, config)
  ),
  getTargetGroups: config => (
    get('geodata/target-group/', config)
  ),
  postTargetGroup: (data, config) => (
    post('geodata/target-group/', data, config)
  ),
  patchTargetGroup: (id, data, config) => (
    patch(`geodata/target-group/${id}/`, data, config)
  ),
  deleteTargetGroup: (id, config) => (
    del(`geodata/target-group/${id}/`, config)
  ),
  postTargetGroupTags: (id, data, config) => (
    post(`geodata/target-group/${id}/tags/`, data, config)
  ),
  deleteTargetGroupTag: (targetGroupId, tagId, config) => (
    del(`geodata/target-group/${targetGroupId}/tags/${tagId}/`, config)
  ),
  postTargetGroupAdditionalInfo: (id, data, config) => (
    post('geodata/additional-info/', { target_group_id: id, ...data }, config)
  ),
  postTargetGroupContactDetail: (id, contactDetail, config) => (
    post('geodata/contact-details/', { target_group_id: id, ...contactDetail }, config)
  ),
  getTargetGroupAnalysis: (id, config = {}) => (
    get(`geodata/target-group/${id}/`, {
      ...config,
      headers: {
        ...(config.headers || {}),
        ...resolveDsHeaders(config),
      },
    })
  ),
  addTargetGroup: (data, config) => (
    post('geodata/target-group/', data, config)
  ),
  getGeometries: (id, config) => (
    get(`geodata/target-group/${id}/geometries/`, config)
  ),
  deleteGeopolygon: (id, config) => (
    del(`geodata/geopolygons/${id}/`, config)
  ),
  postGeopolygon: (data, config) => (
    post('geodata/geopolygons/single/', data, config)
  ),
  postGeopolygons: (data, config) => (
    post('geodata/geopolygons/multiple/', data, config)
  ),
  shareLocationEmail: (data, config) => (
    post('geodata/share-location-email/', data, config)
  ),
  shareLocation: (data, config) => (
    post('geodata/share-location/', data, config)
  ),
  getSharedImage: (id, config) => (
    get(`geodata/shared-image/${id}/`, config)
  ),
  patchGeopolygon: (id, data, config) => (
    patch(`geodata/geopolygons/${id}/`, data, config)
  ),
  patchGeopolygonStatus: (id, data, config) => (
    patch(`geodata/geopolygons/${id}/status/`, data, config)
  ),
  postGeopolygonTags: (id, data, config) => (
    post(`geodata/geopolygons/${id}/tags/`, data, config)
  ),
  deleteGeoPolygonTag: (geoPolygonId, tagId, config) => (
    del(`geodata/geopolygons/${geoPolygonId}/tags/${tagId}/`, config)
  ),
  postGeopolygonAdditionalInfo: (id, data, config) => (
    post('geodata/additional-info/', { geo_polygon_id: id, ...data }, config)
  ),
  postGeopolygonContactDetail: (id, contactDetail, config) => (
    post('geodata/contact-details/', { geo_polygon_id: id, ...contactDetail }, config)
  ),
  patchGeopolygonColor: (shapeId, data) => (
    patch(`geodata/target-group/${shapeId}/update-polygon-color/`, data)
  ),
  getGeopolygon: (id, config = {}) => (
    get(`geodata/geopolygons/${id}/`, {
      ...config,
      headers: {
        ...(config.headers || {}),
        ...resolveDsHeaders(config),
      },
    })
  ),
  getGeopolygonArea: (data, config) => {
    return post('geodata/geopolygons/area/', data, config)
  },
  postBoundaryAnalysis: (data, config) => (
    post('geodata/boundaries/retrieve/', data, config)
  ),
  postAdminBoundaryGeoJSON: (data, config) => (
    post('geodata/boundaries/geometry/', data, config)
  ),
  // for fetching all children boundary polygons
  postAdminBoundaryChildrenPolygons: (data, config) => (
    post('geodata/test-children-ids/', data, config)
  ),
  // for fetching zip code geojson
  postZipCodeBoundaryGeoJSON: (data, config) => (
    post('geodata/boundaries/zip-codes/geometry/', data, config)
  ),
  // for fetching zip code boundary polygon with pop data
  postZipCodeBoundaryAnalysis: (data, config) => (
    post('geodata/boundaries/zip-codes/retrieve/', data, config)
  ),
  // This fetches the zip code list as the name suggests
  getZipCodeList: (ids = [], parent_id = null, config) => {
    const path = ids.length > 0 ? `${ids.join('/')}/` : ''
    return get(`geodata/boundaries/${path}zip-codes/${parent_id || ''}`, config)
  },
  // This fetches the admin level of the country where the zip codes need to displayed instead
  // of whatever already exists at that level like state or sth.
  getZipCodeAdminLevel: (id_0, config) => {
    return get(`geodata/boundaries/country-zip-code-admin-level/?id_0=${id_0}`, config)
  },
  getBoundaries: (ids = [], useZipCodes = false, config = {}) => {
    const path = ids.length > 0 ? `${ids.join('/')}/` : ''
    const query = useZipCodes ? '?zip_codes=true' : '?zip_codes=false'
    return get(`geodata/boundaries/${path}${query}`, config)
  },
  postBoundarySearch: (data, config, zipCodes = false) => {
    const query = zipCodes ? '?zip_codes=true' : '?zip_codes=false'
    return post(`geodata/boundaries/search/${query}`, data, config)
  },
  deleteBoundary: (id, config) => (
    del(`geodata/boundaries/destroy/${id}/`, config)
  ),
  deleteZipCodeBoundary: (id, config) => (
    del(`geodata/boundaries/zip-codes/destroy/${id}/`, config)
  ),
  getTargetGroupBoundaries: (id, config) => (
    get(`geodata/target-group/${id}/boundaries/`, config)
  ),
  // get zip codes
  getTagetGroupZipCodeBoundaries: (id, config) => (
    get(`geodata/target-group/${id}/zip_codes/`, config)
  ),
  // post zip code boundary
  postZipCodeBoundary: (data, config) => (
    post('geodata/boundaries/zip-codes/save/', data, config)
  ),
  postBoundary: (data, config) => (
    post('geodata/boundaries/save/', data, config)
  ),
  patchBoundaryColor: (id, data, config) => (
    patch(`geodata/boundaries/update-color/${id}/`, data, config)
  ),
  patchBoundaryStatus: (id, data, config) => (
    patch(`geodata/boundaries/update-status/${id}/`, data, config)
  ),
  postBoundaryTags: (id, data, config) => (
    post(`geodata/boundaries/tags/${id}/`, data, config)
  ),
  deleteBoundaryTag: (id, tagId, config) => (
    del(`geodata/boundaries/tags/${id}/${tagId}/`, config)
  ),
  postAdminBoundaryAdditionalInfo: (id, data, config) => (
    post('geodata/additional-info/', { admin_boundary_id: id, ...data }, config)
  ),
  postAdminBoundaryContactDetail: (id, contactDetail, config) => (
    post('geodata/contact-details/', { admin_boundary_id: id, ...contactDetail }, config)
  ),
  patchZipCodeBoundaryColor: (id, data, config) => (
    patch(`geodata/boundaries/zip-codes/update-color/${id}/`, data, config)
  ),
  patchZipCodeBoundaryStatus: (id, data, config) => (
    patch(`geodata/boundaries/zip-codes/update-status/${id}/`, data, config)
  ),
  postZipCodeBoundaryTags: (id, data, config) => (
    post(`geodata/boundaries/zip-codes/tags/${id}/`, data, config)
  ),
  deleteZipCodeBoundaryTag: (id, tagId, config) => (
    del(`geodata/boundaries/zip-codes/tags/${id}/${tagId}/`, config)
  ),
  postZipCodeBoundaryAdditionalInfo: (id, data, config) => (
    post('geodata/additional-info/', { zip_code_boundary_id: id, ...data }, config)
  ),
  postZipCodeBoundaryContactDetail: (id, contactDetail, config) => (
    post('geodata/contact-details/', { zip_code_boundary_id: id, ...contactDetail }, config)
  ),
  postTargetGroupShare: (id, data, config) => (
    post(`geodata/target-group/${id}/share/`, data, config)
  ),
  getUserShapefiles: (config) => (
    get('geodata/user-shapefile/', config)
  ),
  getUserShapefileLevel: (id, config) => (
    get(`geodata/user-shapefile/level/${id}/`, config)
  ),
  getUserShapefileLevelGeometries: (id, config) => (
    get(`geodata/user-shapefile/level/${id}/geometries/`, config)
  ),
  postUploadShapefile: (data, config) => (
    post('geodata/user-shapefile/upload/', data, config)
  ),
  deleteShapefile: (id, config) => (
    del(`geodata/user-shapefile/${id}/`, config)
  ),
  getUserShapefileShape: (levelId, shapeId, config) => (
    get(`geodata/user-shapefile/level/${levelId}/shape/${shapeId}/`, config)
  ),
  getUserMarkerfiles: (config) => (
    get('geodata/user-marker-file/', config)
  ),
  getUserMarkerfile: (id, config) => (
    get(`geodata/user-marker-file/${id}/`, config)
  ),
  postUploadMarkerfile: (data, config) => (
    post('geodata/user-marker-file/', data, config)
  ),
  deleteMarkerfile: (id, config) => (
    del(`geodata/user-marker-file/${id}/`, config)
  ),
  postUploadMarkerfileMarkers: (data, config) => (
    post('/geodata/markers/upload/', data, config)
  ),
  postFetchMarkersFallWithin: (data, config) => (
    post('/geodata/markers/fall-within/', data, config)
  ),
  getBoundaryKML: (id, kmz, config) => (
    get(`geodata/boundaries/kml/${id}${kmz ? '?kmz_file' : ''}`, config)
  ),
  getMarkerKML: (id, kmz, config) => (
    get(`geodata/markers/${id}/kml${kmz ? '?kmz_file' : ''}`, config)
  ),
  getRegionKML: (id, kmz, config) => (
    get(`geodata/geopolygons/${id}/kml${kmz ? '?kmz_file' : ''}`, config)
  ),
  getTargetGroupKML: (id, data, config) => (
    post(`geodata/target-group/${id}/kml/`, data, config)
  ),
  getMarkers: (config) => (
    get('geodata/markers/', config)
  ),
  postMarker: (data, config) => (
    post('geodata/markers/', data, config)
  ),
  postMarkersBulk: (data, config) => (
    post('geodata/markers/bulk-create/', data, config)
  ),
  patchMarker: (id, data, config) => (
    patch(`geodata/markers/${id}/`, data, config)
  ),
  deleteMarker: (id, config) => (
    del(`geodata/markers/${id}/`, config)
  ),
  patchMarkerStatus: (id, data, config) => (
    patch(`geodata/markers/${id}/status/`, data, config)
  ),
  patchMarkerName: (id, name, config) => (
    patch(`geodata/markers/${id}/rename/`, { name }, config)
  ),
  postMarkerTags: (id, data, config) => (
    post(`geodata/markers/${id}/tags/`, data, config)
  ),
  deleteMarkerTag: (id, tagId, config) => (
    del(`geodata/markers/${id}/tags/${tagId}/`, config)
  ),
  postMarkerAdditionalInfo: (id, data, config) => (
    post('geodata/additional-info/', { marker_id: id, ...data }, config)
  ),
  postMarkerContactDetail: (id, contactDetail, config) => (
    post('geodata/contact-details/', { marker_id: id, ...contactDetail }, config)
  ),
  postBuffer: (data, config) => (
    post('geodata/geopolygons/buffer/', data, config)
  ),
  postCopyBoundary: (id, data, config) => (
    post(`geodata/boundaries/copy/${id}/`, data, config)
  ),
  postCopyMarker: (id, data, config) => (
    post(`geodata/markers/${id}/copy/`, data, config)
  ),
  postCopyRegion: (id, data, config) => (
    post(`geodata/geopolygons/${id}/copy/`, data, config)
  ),
  postCopyZipCodeBoundary: (id, data, config) => (
    post(`geodata/boundaries/zip-codes/copy/${id}/`, data, config)
  ),
  copyGeopolygonBulk: (data) => (
    post('geodata/geometries/bulk-copy/', data)
  ),
  moveGeometryBulk: (data) => (
    post('geodata/geometries/bulk-move/', data)
  ),
  postBulkBuffer: (data) => (
    post('geodata/bulk/buffer-markers/', data)
  ),
  postBulkIsochrone: (data) => (
    post('geodata/bulk/isochrone-markers/', data)
  ),
  deleteGeoBulk: (config) => (
    del('geodata/geometries/bulk-delete/', config)
  ),
  getFile: (url, config) => getNoAutorization(url, config),
  getMarkerColors: () => get('geodata/colors/'),
  getRecentColors: (count = 3) => get(`geodata/colors/recent?n=${count}`),
  // Manage Status uses one user configuration plus independent shape/folder lists.
  getUserConfigurations: (config) => get('geodata/user-configurations/', config),
  updateUserConfigurations: (data, config) => patch('geodata/user-configurations/', data, config),
  getShapeStatuses: (config) => get('geodata/shape-statuses/', config),
  createShapeStatus: (data, config) => post('geodata/shape-statuses/', data, config),
  updateShapeStatus: (id, data, config) => patch(`geodata/shape-statuses/${id}/`, data, config),
  deleteShapeStatus: (id, config) => del(`geodata/shape-statuses/${id}/`, config),
  reorderShapeStatuses: (data, config) => post('geodata/shape-statuses/reorder/', data, config),
  getFolderStatuses: (config) => get('geodata/folder-statuses/', config),
  createFolderStatus: (data, config) => post('geodata/folder-statuses/', data, config),
  updateFolderStatus: (id, data, config) => patch(`geodata/folder-statuses/${id}/`, data, config),
  deleteFolderStatus: (id, config) => del(`geodata/folder-statuses/${id}/`, config),
  reorderFolderStatuses: (data, config) => post('geodata/folder-statuses/reorder/', data, config),
  getUserTags: (config) => get('geodata/user-tags/', config),
  createUserTag: (data, config) => post('geodata/user-tags/', data, config),
  updateUserTag: (id, data, config) => patch(`geodata/user-tags/${id}/`, data, config),
  deleteUserTag: (id, config) => del(`geodata/user-tags/${id}/`, config),
  getUserAdditionalInfoItems: (config) => get('users/additional-info-items/', config),
  createUserAdditionalInfoItem: (data, config) => post('users/additional-info-items/', data, config),
  updateUserAdditionalInfoItem: (id, data, config) => patch(`users/additional-info-items/${id}/`, data, config),
  deleteUserAdditionalInfoItem: (id, config) => del(`users/additional-info-items/${id}/`, config),
  reorderUserAdditionalInfoItems: (data, config) => post('users/additional-info-items/reorder/', data, config),
  purchasePlan: (planId, config = {}, gaIds = {}) => {
    const query = new URLSearchParams({ plan_id: planId, ...gaIds })
    return get(`plans/purchase/?${query.toString()}`, config)
  },
  getYears: () => get('/geodata/years/'),
  getDefaultYear: () => get('/geodata/years/default/'),
  getFilterDefaults: () => get('/geodata/filters/'),
  getGoogleAutocomplete: async (data) => {
    // Use backend proxy endpoint for autocomplete predictions
    // Note: Autocomplete predictions are NOT counted toward usage limits
    // Only actual searches (text search and nearby search) are tracked
    try {
      const response = await post('poi/autocomplete/', data)
      // Convert axios response to fetch-like response for compatibility
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        json: () => Promise.resolve(response.data),
        statusText: response.statusText
      }
    } catch (error) {
      // Handle axios errors
      if (error.response) {
        return {
          ok: false,
          status: error.response.status,
          json: () => Promise.resolve(error.response.data),
          statusText: error.response.statusText
        }
      }
      throw error
    }
  },
  getGoogleNearbyPlaces: async (payload) => {
    // Use backend proxy endpoint for usage tracking and limit enforcement
    // IMPORTANT: This should call /api/poi/search/nearby/ NOT Google directly
    try {
      console.log('[POI] Calling backend proxy: /api/poi/search/nearby/')
      const response = await post('poi/search/nearby/', payload)
      console.log('[POI] Backend response received:', response.status)
      // Convert axios response to fetch-like response for compatibility
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        json: () => Promise.resolve(response.data),
        statusText: response.statusText
      }
    } catch (error) {
      console.error('[POI] Backend proxy error:', error)
      // Handle axios errors
      if (error.response) {
        return {
          ok: false,
          status: error.response.status,
          json: () => Promise.resolve(error.response.data),
          statusText: error.response.statusText
        }
      }
      throw error
    }
  },
  // Fetches all places according to a search input
  fetchPlacesSearchByText: async (query, apiKey, _PLACES_REQUEST_FIELDS, polygonBounds, mapBounds, maxResultCount = 20) => {
    // Use backend proxy endpoint for usage tracking and limit enforcement
    // IMPORTANT: This should call /api/poi/search/text/ NOT Google directly
    const bodyObj = {
      textQuery: query,
      maxResultCount
    }
    // Only add locationRestriction if bounds are provided
    if (polygonBounds) {
      bodyObj.locationRestriction = polygonBounds
    } else if (mapBounds) {
      bodyObj.locationRestriction = mapBounds
    }

    try {
      console.log('[POI] Calling backend proxy: /api/poi/search/text/')
      const response = await post('poi/search/text/', bodyObj)
      console.log('[POI] Backend response received:', response.status)
      // Return places array from response
      return response.data.places || []
    } catch (error) {
      console.error('[POI] Backend proxy error:', error)
      // Handle limit exceeded or other errors
      if (error.response && error.response.data) {
        const errorData = error.response.data
        if (errorData.error === 'limit_exceeded') {
          // Create a custom error with the limit_exceeded flag so frontend can detect it
          const limitError = new Error(errorData.message)
          limitError.isLimitExceeded = true
          limitError.errorData = errorData
          throw limitError
        }
        throw new Error(errorData.message || errorData.error || 'Failed to fetch places search by text')
      }
      throw new Error('Failed to fetch places search by text')
    }
  },

  // Fetch the details of a place, using its placeId (via backend proxy)
  getGooglePlaceDetails: async (placeId) => {
    // Use backend proxy endpoint for usage tracking and limit enforcement
    // IMPORTANT: This should call /api/poi/details/ NOT Google directly
    try {
      console.log('[POI] Calling backend proxy: /api/poi/details/')
      const response = await post('poi/details/', { placeId })
      console.log('[POI] Backend response received:', response.status)
      // Convert axios response to fetch-like response for compatibility
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        json: () => Promise.resolve(response.data),
        statusText: response.statusText
      }
    } catch (error) {
      console.error('[POI] Backend proxy error:', error)
      // Handle axios errors
      if (error.response) {
        return {
          ok: false,
          status: error.response.status,
          json: () => Promise.resolve(error.response.data),
          statusText: error.response.statusText
        }
      }
      throw error
    }
  },
  // Fetch the details of a place, using its placeId (legacy - calls Google directly)
  fetchPlaceDetails: async (placeId, apiKey, PLACES_REQUEST_FIELDS) => {
    const url = `https://places.googleapis.com/v1/places/${placeId}?fields=${PLACES_REQUEST_FIELDS.join(',')}`
    const headers = {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey
    }
    const res = await fetch(url, {
      method: 'GET',
      headers
    })
    if (!res.ok) throw new Error('Failed to fetch place details')
    const data = await res.json()
    return {
      place_id: data.id,
      description: 'description',
      name: data.displayName.text || '',
      address: data.formattedAddress || '',
      latitude: data.location.latitude,
      longitude: data.location.longitude,
      types: data.types || [],
      type: Array.isArray(data.types) && data.types.length ? data.types[0] : '',
      iconMaskBaseUri: data.iconMaskBaseUri || null,
      icon_url: data.iconMaskBaseUri || null,
      color: 1,
      visible: true,
      is_competitor: false,
      is_ally: false
    }
  },
  getPopulationTaskPolling: (taskId, config) => (
    get(`geodata/polling/population-data/${taskId}/`, config)
  ),
  getSignaturePolygonPopulatonTaskPolling: (taskId, config) => (
    get(`geodata/polling/population-data/signature-polygon/${taskId}/`, config)
  ),
  pollWorldpopTask: ({ targetGroupId, geoPolygonId } = {}, config = {}) => {
    const params = new URLSearchParams()
    if (geoPolygonId != null) {
      params.set('geo_polygon_id', geoPolygonId)
    } else if (targetGroupId != null) {
      params.set('target_group_id', targetGroupId)
    }
    return get(`geodata/polling/worldpop-task/?${params.toString()}`, config)
  },
  getPopDataForTargetGroup: (targetGroupId, config = {}) => {
    return get(`geodata/population-data/target-groups/?target_group=${targetGroupId}`, config)
  },
  getDataForAllPolygons: (targetGroupId, config = {}) => {
    return get(`geodata/population-data/geometries/?target_group=${targetGroupId}`, config)
  },
  getDataForPolygon: (geometryId, config = {}) => {
    return get(`geodata/population-data/geometries/?geometry=${geometryId}`, config)
  },
  getPopDataForAdminBoundary: (remoteDbId, config = {}) => {
    return get(
      `geodata/population-data/admin-boundaries/?remote_db_id=${remoteDbId}`,
      config
    )
  },
  getPopDataForZipBoundary: (remoteDbId, config = {}) => {
    return get(
      `geodata/population-data/zip-boundaries/?remote_db_id=${remoteDbId}`,
      config
    )
  },
}
